
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import SettingsDelete from '@components/settings/SettingsDelete.vue';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import { ruleV2Icons } from '@util/settingIcons'
import { LoadState } from '@util/types';
import { PilotMutation } from '@/generated-clients/myTrips';
import { PilotWithRules } from '@/models/SettingsModels';
import deepCopy from '@/utils/copy';
import AppEditableHeader from '@components/new-styleguide/AppEditableHeader.vue';
import ColorPicker from '@components/settings/ColorPicker.vue';
import { channelSubtitle } from '@util/channelSubtitle';
import goBackWithFallback from '@util/goBackWithFallback';
import { RouterFrom } from '@/router/Routes';
import { Location } from 'vue-router';

const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: 'PilotEdit',
  components: {
    AppPage,
    AppContent,
    AppEditableHeader,
    SettingsTitleBar,
    SettingsFooter,
    SettingsDelete,
    ColorPicker,
    SettingsPanel,
    AppListItem,
    InfoBoxWarning
  }
})
export default class PilotEdit extends Vue {
  @SettingsModule.Getter('getPilots')
  private pilots: PilotWithRules[];

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Action('updatePilot')
  private updatePilot;

  @SettingsModule.Action('deletePilot')
  private deletePilot;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @RouterModule.State('from')
  private from: RouterFrom;

  private input: PilotMutation = {
    firstname: '',
    lastname: '',
    nickname: '',
    initial: '',
    color: ''
  };

  private disabled = true;
  private errorMessage = '';

  private deleteText = 'Sind Sie sicher, dass Sie die Person löschen möchten?';

  private titleDirty = false

  async created() {
    const fetchNonPilotsPromise = Promise.all[this.fetchRules(), this.fetchTags()];

    await this.fetchPilots();

    try {
      await fetchNonPilotsPromise;
    } finally {
      this.input = deepCopy(this.pilot);
      this.disabled = false;
    }
  }

  get rulesHeadline() {
    return 'Diese Person ist Teil folgender Regel' + (this.associatedRuleItems.length > 1 ? 'n' : '');
  }

  get titleValue() {
    if (this.isLoading) {
      return '';
    } else if (this.titleDirty) {
      return this.input.nickname;
    } else {
      return this.input.nickname || `${this.input.firstname} ${this.input.lastname}`;
    }
  }

  get placeholder() {
    return this.isLoading ? 'Wird geladen...' : 'Name hier eingeben';
  }

  get backButton() {
    return { text: 'Zurück zu Personen', location: this.backLocation };
  }

  get backLocation(): Location {
    return { name: 'pilotsOverview' };
  }

  get pilot() {
    return this.pilots.find(p => p.id === this.id)
  }

  get associatedRuleItems(): ListItem[] {
    return (this.pilot)
        ? this.pilot.associatedRules
            .map(r => ({
              id: r.id,
              title: r.name,
              subtitle: channelSubtitle(r), ...ruleV2Icons(r),
              location: { name: 'ruleEdit', params: { id: r.id.toString() } },
              hoverText: 'Regel bearbeiten'
            })
          )
        : [];
  }

  get id() {
    return parseInt(this.$route.params.id);
  }

  get nameError() {
    return this.input && this.input.firstname.length === 0 && this.input.nickname.length === 0;
  }

  get initialError() {
    return this.input && this.input.initial.length !== 1;
  }

  get errorsExist() {
    return this.nameError || this.initialError;
  }

  get errorLoading() {
    return this.pilotsStatus === LoadState.Error;
  }

  get showAssociatedRules() {
    return !this.isLoading && this.associatedRuleItems.length > 0;
  }

  get isLoading() {
    return this.pilotsStatus === LoadState.Loading;
  }

  private nameChange(name) {
    this.titleDirty = true;
    this.input.nickname = name;
    if (this.input.nickname.length >= 1) {
      this.input.initial = this.input.nickname[0].trim().toUpperCase();
    }
  }

  private async submit() {
    if (!this.errorsExist) {
      this.disabled = true;

      try {
        await this.updatePilot({ id: this.pilot.id, pilot: this.input });
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        this.errorMessage = 'Fehler beim Speichern der Person. Bitte versuchen sie es später erneut.'
      } finally {
        this.disabled = false;
      }
    }
  }

  private deleteRuleButton() {
    if (window.confirm(this.deleteText)) {
      this.handlePilotDelete();
    }
  }

  private async handlePilotDelete() {
    this.disabled = true;
    try {
      await this.deletePilot({ id: this.pilot.id });
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    } catch (error) {
      this.errorMessage = 'Fehler beim Löschen der Person. Bitte versuchen sie es später erneut.'
    } finally {
      this.disabled = false;
    }
  }
}
